export const formatDate = (dateString: string) => {
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
  };
  return new Date(dateString).toLocaleDateString(undefined, options);
};

export const formatDateLong = (dateString: string) => {
  const date = new Date(dateString?.trim());

  if (isNaN(date.getTime())) {
    return ""; // invalid date fallback
  }

  const options: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
    hour12: true,
  };

  return date.toLocaleString("en-GB", options);
};

export const capitalizeFirstLetter = (str?: string): string | undefined => {
  if (str === undefined) {
    return undefined;
  }
  return str.charAt(0).toUpperCase() + str?.slice(1);
};

export function formatCurrency(
  value: number | undefined | null,
  currency: string,
  display: "code" | "symbol"
) {
  try {
    if (value != null) {
      return value.toLocaleString("en-NG", {
        style: "currency",
        currency,
        minimumFractionDigits: 2,
        currencyDisplay: display,
      });
    } else {
    }
  } catch (e) {
    return "N/A";
  }
}

// Safely coerce a total (which may arrive as a number or a comma-formatted
// string) into a number, defaulting to 0.
export const toNumber = (value: any): number =>
  Number(String(value ?? "").replace(/,/g, "")) || 0;

// Format a non-monetary total with thousands separators.
export const formatCount = (value: any): string =>
  toNumber(value).toLocaleString();

// Format a monetary total with the Naira symbol.
export const formatNaira = (value: any): string =>
  formatCurrency(toNumber(value), "NGN", "symbol") ?? "₦0.00";

const baseURL =
  process.env.REACT_APP_BACKEND_URL ||
  "https://staging-api.admin.vendstash.com";

// Download a file (e.g. CSV export) from an authenticated endpoint. Unlike
// useRequest, this reads the response as a blob so it can handle raw file
// bodies. Honours the server's Content-Disposition filename when present and
// throws with the backend's error message so callers can toast it (e.g. the
// "no data to export" 500 the CSV serialiser raises on an empty result set).
export const downloadCsv = async (
  path: string,
  fallbackName: string
): Promise<void> => {
  const token = localStorage.getItem("token");
  const res = await fetch(`${baseURL}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) {
    // Error responses come back as JSON, not a file — surface the message.
    let message = "Export failed, please try again";
    try {
      const body = await res.json();
      if (body?.message) message = body.message;
    } catch {
      /* non-JSON error body — keep the default message */
    }
    throw new Error(message);
  }

  const blob = await res.blob();
  const disposition = res.headers.get("Content-Disposition") || "";
  const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  const filename = match ? decodeURIComponent(match[1]) : fallbackName;

  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
};
